import { BlockScroll } from "@birdwatch/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useEffectEvent } from "react";
import { Button } from "@/components/ui/button/button";
import { BwCloseLine } from "@/components/ui/icons";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { APP_ROUTERS } from "@/constants/app-routers";
import { usePrefetchMyPlaces } from "@/features/my/_hooks/use-prefetch-my-places";
import { useMyPlaceContext } from "@/features/my/_providers/my-place-context-provider";
import { PlaceSwitcherItem } from "./place-switcher-item";

export type PlaceSwitcherDrawerProps = {
  open: boolean;
  onClose: () => void;
};

export function PlaceSwitcherDrawer({
  open,
  onClose,
}: PlaceSwitcherDrawerProps) {
  const navigate = useNavigate();

  const { selectedPlaceId, places, isLoading } = useMyPlaceContext();
  // Warm the homes once per opening, not again whenever the places list refetches.
  const prefetchPlaces = useEffectEvent(usePrefetchMyPlaces());

  useEffect(() => {
    if (open) prefetchPlaces();
  }, [open]);

  // If no places exist, redirect to places page
  useEffect(() => {
    if (!isLoading && places.length === 0) {
      navigate({
        to: APP_ROUTERS.MY_PLACES,
        search: {
          addOns: [],
        },
      });
    }
  }, [isLoading, places.length, navigate]);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side="left"
        className="w-full flex flex-col overflow-hidden border-none sm:max-w-112"
      >
        <SheetHeader className="flex flex-row items-center justify-between shrink-0">
          <SheetTitle>Places</SheetTitle>
          <SheetClose asChild>
            <Button
              kind="ghost"
              variant="neutral"
              size="icon-md"
              className="shrink-0"
            >
              <BwCloseLine size={20} />
            </Button>
          </SheetClose>
        </SheetHeader>
        <BlockScroll className="flex-1">
          <div className="px-6 py-1">
            <span className="text-sm text-text-dark-high">
              ↴ Select to switch the place.
            </span>
            <div className="py-4">
              {places.map((place) => {
                const isActive = String(place.id) === String(selectedPlaceId);
                return (
                  <PlaceSwitcherItem
                    key={place.id}
                    place={place}
                    isActive={isActive}
                    onClick={onClose}
                  />
                );
              })}
            </div>
          </div>
        </BlockScroll>
      </SheetContent>
    </Sheet>
  );
}
